// Browser detection utility functions

export interface BrowserInfo {
  name: string;
  version: string;
  platform: string;
}

/**
 * Get browser name and version information
 */
export function getBrowserName(): BrowserInfo {
  const userAgent = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';
  let platform = navigator.platform;

  // Chrome
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browserName = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Edge
  else if (userAgent.includes('Edg')) {
    browserName = 'Edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Firefox
  else if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Safari
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browserName = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Mozilla (fallback for other Gecko-based browsers)
  else if (userAgent.includes('Mozilla')) {
    browserName = 'Mozilla';
    const match = userAgent.match(/rv:(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }

  return {
    name: browserName,
    version: browserVersion,
    platform: platform
  };
}

/**
 * Check if browser supports WebRTC
 */
export function isWebRTCSupported(): boolean {
  return !!(
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    window.RTCPeerConnection
  );
}

/**
 * Check if browser supports Web Audio API
 */
export function isWebAudioSupported(): boolean {
  return !!(window.AudioContext || (window as any).webkitAudioContext);
}

/**
 * Check if browser is supported for video interviews
 */
export function isBrowserSupported(): boolean {
  const { name } = getBrowserName();
  const supportedBrowsers = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Mozilla'];
  
  return supportedBrowsers.includes(name) && 
         isWebRTCSupported() && 
         isWebAudioSupported();
}

/**
 * Get detailed browser capabilities
 */
export function getBrowserCapabilities(): {
  webRTC: boolean;
  webAudio: boolean;
  mediaDevices: boolean;
  getUserMedia: boolean;
  setSinkId: boolean;
  screen_capture: boolean;
} {
  return {
    webRTC: isWebRTCSupported(),
    webAudio: isWebAudioSupported(),
    mediaDevices: !!(navigator.mediaDevices),
    getUserMedia: !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function'),
    setSinkId: 'setSinkId' in HTMLMediaElement.prototype,
    screen_capture: !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia)
  };
}
